(() => {
  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  const strings = window.themeStrings || {};
  const routes = window.themeRoutes || {};
  const assets = window.themeAssets || {};

  document.documentElement.classList.remove('no-js');

  function escapeHTML(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[char]);
  }

  function formatMoney(cents) {
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents);
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency
    }).format(Number(cents || 0) / 100);
  }

  function imageDimension(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? Math.round(number) : fallback;
  }

  function trapFocus(container) {
    if (!container || container.dataset.focusTrapBound === 'true') return;
    const selectors = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const focusables = qsa(selectors, container);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    container.dataset.focusTrapBound = 'true';
    container.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  const mobileMenu = qs('[data-main-menu]');
  const menuPanel = mobileMenu ? qs('.mobile-menu__panel', mobileMenu) : null;
  const setMenuOpen = (open) => {
    if (!mobileMenu) return;
    mobileMenu.classList.toggle('is-open', open);
    mobileMenu.setAttribute('aria-hidden', String(!open));
    qsa('[data-menu-toggle]').forEach((button) => button.setAttribute('aria-expanded', String(open)));
    document.body.classList.toggle('overflow-hidden', open);
    if (open) {
      trapFocus(menuPanel || mobileMenu);
      qs('a, button, input', menuPanel || mobileMenu)?.focus();
    }
  };

  qsa('[data-menu-toggle]').forEach((button) => {
    button.addEventListener('click', () => setMenuOpen(!mobileMenu?.classList.contains('is-open')));
  });

  qsa('.mobile-menu__nav a').forEach((link) => {
    link.addEventListener('click', () => setMenuOpen(false));
  });

  const searchModal = qs('[data-search-modal]');
  const openSearch = () => {
    if (!searchModal) return;
    searchModal.hidden = false;
    trapFocus(searchModal);
    qs('input[type="search"]', searchModal)?.focus();
  };
  const closeSearch = () => {
    if (searchModal) searchModal.hidden = true;
  };

  qsa('[data-search-open]').forEach((button) => button.addEventListener('click', openSearch));
  qsa('[data-search-close]').forEach((button) => button.addEventListener('click', closeSearch));

  qsa('[data-carousel]').forEach((carousel) => {
    const track = qs('[data-carousel-track]', carousel);
    if (!track) return;
    const scrollByAmount = () => Math.max(track.clientWidth * 0.85, 280);
    qs('[data-carousel-prev]', carousel)?.addEventListener('click', () => {
      track.scrollBy({ left: -scrollByAmount(), behavior: 'smooth' });
    });
    qs('[data-carousel-next]', carousel)?.addEventListener('click', () => {
      track.scrollBy({ left: scrollByAmount(), behavior: 'smooth' });
    });
  });

  const drawer = qs('[data-cart-drawer]');
  const drawerPanel = () => qs('.cart-drawer__panel', drawer);
  const drawerBody = () => qs('[data-cart-drawer-body]', drawer);

  const openCartDrawer = () => {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    trapFocus(drawerPanel());
    drawerPanel()?.focus();
  };

  const closeCartDrawer = () => {
    drawer?.setAttribute('aria-hidden', 'true');
  };

  function renderCartDrawer(cart) {
    if (!drawer || !cart) return;
    const body = drawerBody();
    if (!body) return;

    qsa('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
    });
    qsa('[data-cart-subtotal]').forEach((subtotal) => {
      subtotal.textContent = formatMoney(cart.total_price);
    });

    if (!cart.item_count) {
      body.innerHTML = `<p>${escapeHTML(strings.cartEmpty || 'Your cart is currently empty.')}</p>`;
      return;
    }

    const items = cart.items.map((item) => {
      const image = item.image || item.featured_image?.url || '';
      const imageAlt = item.featured_image?.alt || item.product_title || '';
      const imageWidth = imageDimension(item.featured_image?.width, 240);
      const imageHeight = imageDimension(item.featured_image?.height, 240);
      const imageMarkup = image
        ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(imageAlt)}" width="${imageWidth}" height="${imageHeight}" loading="lazy">`
        : (assets.productPlaceholder ? `<img src="${escapeHTML(assets.productPlaceholder)}" alt="Product image placeholder" width="800" height="800" loading="lazy">` : '');
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-item__variant">${escapeHTML(item.variant_title)}</p>`
        : '';
      return `
        <div class="cart-item" data-cart-item-key="${escapeHTML(item.key)}">
          <a class="cart-item__media" href="${escapeHTML(item.url)}" aria-label="${escapeHTML(item.product_title)}">
            ${imageMarkup}
          </a>
          <div class="cart-item__details">
            <a href="${escapeHTML(item.url)}" class="cart-item__title">${escapeHTML(item.product_title)}</a>
            ${variant}
            <div class="cart-item__price">${formatMoney(item.final_line_price)}</div>
            <div class="cart-item__quantity">
              <label for="updates_${escapeHTML(item.key)}" class="visually-hidden">${escapeHTML(strings.quantityLabel || 'Quantity')}</label>
              <input id="updates_${escapeHTML(item.key)}" type="number" name="updates[]" value="${item.quantity}" min="0" inputmode="numeric" data-cart-quantity-input>
              <a href="/cart/change?id=${encodeURIComponent(item.key)}&quantity=0" class="cart-item__remove">${escapeHTML(strings.remove || 'Remove')}</a>
            </div>
          </div>
        </div>`;
    }).join('');

    body.innerHTML = `
      <form action="${escapeHTML(routes.cartUrl || '/cart')}" method="post" class="cart-drawer__form" data-cart-drawer-form>
        <div class="cart-items cart-items--drawer">${items}</div>
        <button type="submit" name="update" class="button button--secondary button--full">${escapeHTML(strings.updateCart || 'Update cart')}</button>
      </form>`;
  }

  qsa('[data-cart-open]').forEach((element) => element.addEventListener('click', (event) => {
    event.preventDefault();
    openCartDrawer();
  }));
  qsa('[data-cart-close]').forEach((element) => element.addEventListener('click', closeCartDrawer));

  qsa('[data-qty-minus], [data-qty-plus]').forEach((button) => {
    button.addEventListener('click', () => {
      const input = button.parentElement.querySelector('input');
      if (!input) return;
      const min = Number(input.getAttribute('min') || 1);
      const current = Number(input.value || min || 0);
      input.value = button.hasAttribute('data-qty-minus') ? Math.max(min, current - 1) : current + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  const filtersPanel = qs('[data-filters-panel]');
  const setFiltersOpen = (open) => {
    if (!filtersPanel) return;
    filtersPanel.classList.toggle('is-open', open);
    qsa('[data-filters-open]').forEach((button) => button.setAttribute('aria-expanded', String(open)));
    document.body.classList.toggle('overflow-hidden', open);
    if (open) {
      trapFocus(filtersPanel);
      qs('button, input, select, a', filtersPanel)?.focus();
    }
  };

  qsa('[data-filters-open]').forEach((button) => {
    button.addEventListener('click', () => setFiltersOpen(true));
  });

  qsa('[data-filters-close]').forEach((button) => {
    button.addEventListener('click', () => setFiltersOpen(false));
  });

  qsa('[data-media-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const id = thumb.getAttribute('data-media-thumb');
      const gallery = thumb.closest('.product-gallery');
      if (!gallery) return;
      qsa('.product-gallery__slide', gallery).forEach((slide) => slide.classList.toggle('is-active', slide.dataset.mediaId === id));
      qsa('[data-media-thumb]', gallery).forEach((item) => item.classList.toggle('is-active', item === thumb));
    });
  });

  qsa('[data-product-section]').forEach((section) => {
    let product;
    try {
      product = JSON.parse(section.getAttribute('data-product-json'));
    } catch (error) {
      return;
    }

    const form = qs('[data-product-form]', section);
    if (!form) return;

    const idInput = qs('[data-variant-id-input]', section);
    const addButton = qs('[data-add-to-cart]', section);
    const addText = qs('[data-add-to-cart-text]', section);
    const inventory = qs('[data-inventory-status]', section);
    const priceEl = qs('[data-price]', section);
    const skuWrapper = qs('[data-sku-wrapper]', section);
    const skuEl = qs('[data-sku]', section);

    const renderPrice = (variant) => {
      if (!priceEl || !variant) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      priceEl.classList.toggle('price--on-sale', onSale);
      priceEl.classList.toggle('price--sold-out', !variant.available);
      priceEl.innerHTML = `<span class="price__regular">${formatMoney(variant.price)}</span>${
        onSale ? `<span class="price__compare">${formatMoney(variant.compare_at_price)}</span>` : ''
      }`;
    };

    const activateMedia = (mediaId) => {
      if (!mediaId) return;
      const gallery = qs('.product-gallery', section);
      if (!gallery) return;
      qsa('.product-gallery__slide', gallery).forEach((slide) => slide.classList.toggle('is-active', slide.dataset.mediaId === String(mediaId)));
      qsa('[data-media-thumb]', gallery).forEach((thumb) => thumb.classList.toggle('is-active', thumb.getAttribute('data-media-thumb') === String(mediaId)));
    };

    const updateVariant = () => {
      const selected = qsa('[data-option-input]:checked', form).map((input) => input.value);
      const variant = product.variants.find((candidate) => selected.every((value, index) => candidate.options[index] === value));
      if (!variant) {
        if (idInput) idInput.value = '';
        if (inventory) inventory.textContent = strings.unavailable || 'Unavailable';
        if (skuWrapper && skuEl) {
          skuEl.textContent = '';
          skuWrapper.hidden = true;
        }
        if (addButton) addButton.disabled = true;
        if (addText) addText.textContent = strings.unavailable || 'Unavailable';
        return;
      }
      if (idInput) idInput.value = variant.id;
      renderPrice(variant);
      if (inventory) inventory.textContent = variant.available ? (strings.inStock || 'In stock') : (strings.soldOut || 'Sold out');
      if (skuWrapper && skuEl) {
        skuEl.textContent = variant.sku || '';
        skuWrapper.hidden = !variant.sku;
      }
      if (addButton) addButton.disabled = !variant.available;
      if (addText) addText.textContent = variant.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');
      activateMedia(variant.featured_media?.id);
    };

    qsa('[data-option-input]', form).forEach((input) => input.addEventListener('change', updateVariant));

    form.addEventListener('submit', async (event) => {
      if (!drawer) return;
      const submitter = event.submitter || document.activeElement;
      if (submitter && submitter !== addButton && submitter.name !== 'add') return;
      event.preventDefault();
      addButton?.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Cart add failed');
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((res) => res.json());
        renderCartDrawer(cart);
        openCartDrawer();
      } catch (error) {
        form.submit();
      } finally {
        addButton?.removeAttribute('aria-busy');
      }
    });
  });

  qs('[data-filters-toggle]')?.addEventListener('click', () => {
    qs('[data-filters-panel]')?.classList.toggle('is-open');
  });

  qsa('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', (event) => {
      const form = event.target.closest('form');
      if (form) {
        form.submit();
        return;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      window.location.href = url.toString();
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeSearch();
    closeCartDrawer();
    setMenuOpen(false);
    setFiltersOpen(false);
  });
})();
